import {Badge, Box, Button, CloseButton, createStyles, Popover, Text} from '@mantine/core';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import {isNodeTesting} from 'lib/Browser';
import localStorage from 'LocalStorage';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';

import SuggestedPlotsCalloutIllustration from './SuggestedPlotsCalloutIllustration';

const LLM_ENABLE_SUGGESTED_PLOTS_CALLOUT_FLAG = 'LLM_ENABLE_SUGGESTED_PLOTS_CALLOUT';
const STORAGE_KEY = 'benchling.ai.suggested-plots-callout-dismissed';
const MIN_ROWS_FOR_CALLOUT = 3;

// CSS class applied to the notebook editor's scroll container (see
// notebook/notes/core-ui/NoteEditor.tsx). The callout portals into it so it scrolls with the
// entry; we fall back to document.body when it isn't present (e.g. document previews).
const NOTE_EDITOR_SCROLL_CONTAINER_CLASS = 'noteEditor-scrollContainer';
// Attribute on the editor root (see NoteEditor.tsx) used to scope the scroll-container lookup to the
// editor that owns this callout, so split-screen / multi-editor layouts portal into the correct pane.
const FILE_ID_ATTR = 'data-file-id';

// Resolve the scroll container for the editor owning `fileId`, mirroring the scoping in
// notebook/notes/util/NoteEditorScrollAnchor.ts: prefer the container scoped by `data-file-id`, fall
// back to the first container in the document, then to document.body (e.g. document previews).
function findPortalTarget(fileId: string): HTMLElement {
  const scoped = document.querySelector<HTMLElement>(
    `[${FILE_ID_ATTR}="${CSS.escape(fileId)}"] .${NOTE_EDITOR_SCROLL_CONTAINER_CLASS}`
  );
  return (
    scoped ?? document.querySelector<HTMLElement>(`.${NOTE_EDITOR_SCROLL_CONTAINER_CLASS}`) ?? document.body
  );
}

/**
 * Computes whether a "Suggest plots" button is eligible to host the onboarding callout. Centralized
 * here so plain and structured tables stay in sync; callers pass the number of data rows and, when
 * the concept applies, whether the table is collapsed.
 */
export function isCalloutEligible({
  rowCount,
  isCollapsed = false,
}: {
  rowCount: number;
  isCollapsed?: boolean;
}): boolean {
  return !isCollapsed && rowCount >= MIN_ROWS_FOR_CALLOUT;
}

// At most one callout shows across the page at a time. Coordination lives in a small subscribable
// store rather than ad-hoc module flags so that, when the displaying instance unmounts or becomes
// ineligible, the remaining eligible instances are notified and one of them takes over. Dismissal
// is also part of the store so clicking "Got it" hides the callout everywhere, not just locally.
const candidates: Array<symbol> = [];
const listeners = new Set<() => void>();
let dismissed: boolean | null = null;

function emitChange(): void {
  listeners.forEach((listener) => {
    listener();
  });
}

function subscribeToCallout(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function registerCandidate(id: symbol): void {
  if (!candidates.includes(id)) {
    candidates.push(id);
    emitChange();
  }
}

function unregisterCandidate(id: symbol): void {
  const index = candidates.indexOf(id);
  if (index !== -1) {
    candidates.splice(index, 1);
    emitChange();
  }
}

function isCalloutDismissed(): boolean {
  if (dismissed == null) {
    dismissed = localStorage.json.getItem(STORAGE_KEY) === true;
  }
  return dismissed;
}

// Snapshot for useSyncExternalStore: the instance that should display the callout, or null. Returns
// a stable value between calls (same symbol, or null) as the hook requires.
function getCalloutOwner(): symbol | null {
  if (isCalloutDismissed()) {
    return null;
  }
  return candidates.length > 0 ? candidates[0] : null;
}

function dismissCallout(): void {
  trackEvent(ELN_ANALYTICS_ACTIONS.DISMISSED_AI_SUGGESTED_PLOTS_CALLOUT);
  localStorage.json.setItem(STORAGE_KEY, true);
  dismissed = true;
  emitChange();
}

function isCalloutFlagEnabled(): boolean {
  if (isNodeTesting) {
    return false;
  }
  if (!localStorage.enabled()) {
    return false;
  }
  if (!window.ldClient?.ready) {
    return false;
  }
  return window.ldClient.variation(LLM_ENABLE_SUGGESTED_PLOTS_CALLOUT_FLAG, false) === true;
}

// Stable, unique identity for the lifetime of the instance, used as the coordination key. A symbol
// is allocated once via a lazy ref so we don't depend on a module-level counter.
function useInstanceId(): symbol {
  const ref = useRef<symbol | null>(null);
  if (ref.current == null) {
    ref.current = Symbol('suggested-plots-callout');
  }
  return ref.current;
}

function useSuggestedPlotsCallout(isEligible: boolean): {showCallout: boolean; dismiss: () => void} {
  const instanceId = useInstanceId();
  const [flagEnabled] = useState(isCalloutFlagEnabled);
  const isCandidate = flagEnabled && isEligible;

  useEffect(() => {
    if (!isCandidate) {
      return undefined;
    }
    registerCandidate(instanceId);
    return () => {
      unregisterCandidate(instanceId);
    };
  }, [isCandidate, instanceId]);

  const owner = useSyncExternalStore(subscribeToCallout, getCalloutOwner);
  const showCallout = isCandidate && owner === instanceId;

  return {showCallout, dismiss: dismissCallout};
}

const useStyles = createStyles((theme) => ({
  dropdown: {
    borderRadius: 12,
    padding: 0,
    minWidth: 360,
    border: `1px solid ${theme.colors.gray[3]}`,
    boxShadow: '0 24px 48px -16px rgba(15, 18, 40, 0.28), 0 4px 12px rgba(15, 18, 40, 0.08)',
  },
  target: {
    display: 'inline-flex',
    lineHeight: 0,
    '& > button': {
      boxShadow: `0 0 0 2px ${theme.colors.blue[4]}, 0 0 6px 1px ${theme.colors.blue[2]}`,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '14px 14px 10px 18px',
    gap: 8,
  },
  badge: {
    textTransform: 'uppercase',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.04em',
    padding: '2px 6px',
    height: 'auto',
    lineHeight: 1.4,
    marginTop: 3,
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
    flex: 1,
  },
  description: {
    padding: '14px 18px 4px',
    fontSize: 13,
    lineHeight: 1.5,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '8px 14px 14px',
  },
}));

interface SuggestedPlotsCalloutProps {
  children: JSX.Element;
  isEligible: boolean;
  /** Id of the entry/document whose editor owns this callout; scopes the portal to the right pane. */
  fileId: string;
}

export default function SuggestedPlotsCallout({
  children,
  isEligible,
  fileId,
}: SuggestedPlotsCalloutProps): JSX.Element {
  // Print preview drives PDF export and the printer-friendly view (see
  // PrinterFriendlyViewUtils.editorPropertiesContextForPrintView). Suppressing the callout here, at
  // the single shared render point, guarantees it can never leak into an export from any callsite —
  // an ungated callout would otherwise render across every backup of an entry.
  const {isPrintPreviewView} = EditorPropertiesContext.useContext();
  const {showCallout, dismiss} = useSuggestedPlotsCallout(isEligible && !isPrintPreviewView);
  const {classes} = useStyles();
  const portalTarget = useMemo(() => findPortalTarget(fileId), [fileId]);
  if (!showCallout) {
    return children;
  }

  return (
    <Popover
      opened={true}
      position="bottom"
      withArrow={true}
      arrowSize={14}
      shadow="md"
      width={360}
      zIndex={900}
      withinPortal={true}
      portalProps={{target: portalTarget}}
    >
      <Popover.Target>
        <Box component="span" className={classes.target}>
          {children}
        </Box>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <div className={classes.header}>
          <Badge className={classes.badge} variant="light" color="blue" radius={3}>
            New
          </Badge>
          <Text className={classes.title}>AI Suggested Plots</Text>
          <CloseButton size="sm" aria-label="Dismiss" onClick={dismiss} />
        </div>
        <SuggestedPlotsCalloutIllustration />
        <Text className={classes.description}>
          Generate interactive plots and charts from your table data and save them to your entry.
        </Text>
        <div className={classes.footer}>
          <Button size="sm" onClick={dismiss}>
            Got it
          </Button>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
}

export function resetCalloutClaimForTesting(): void {
  candidates.length = 0;
  listeners.clear();
  dismissed = null;
}
